import '../styles/pdf-pages.css';
import '../styles/model-overview.css';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateFestivalCo2 } from '@/domain/impact-calculate-festival/entities/calculate-festival-co2.entity';
import { CalculateFestivalEnv } from '@/domain/impact-calculate-festival/entities/calculate-festival-env.entity';
import {
  selectImpactCalculateFestivalCo2ForProject,
  selectImpactCalculateFestivalEnvForProject,
} from '@/domain/impact-calculate-festival/impact-calculate-festival.selectors';
import { selectCurrentProjectFestival } from '@/domain/project-festival/project-festival.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { formatProjectDatesTz } from '@/ui/utils/format-project-dates';

import CalendarMonthSvg from '../assets/calendar_month.svg';
import { LittleGauge } from '../components/LittleGauge';
import { PdfProjectPager } from '../components/PdfProjectPager';
import { PdfScoreCard } from '../components/PdfScoreCard';
import { displayDecimal } from '../utils/display-scores.utils';

export function PdfFestivalPage3({ totalPages }: { totalPages: number }) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const festival = useAppSelector(selectCurrentProjectFestival);
  const festivalId = festival?.id ?? 0;

  const impactEnv = useAppSelector(
    selectImpactCalculateFestivalEnvForProject(festivalId),
  ) satisfies CalculateFestivalEnv;

  const impactCo2 = useAppSelector(
    selectImpactCalculateFestivalCo2ForProject(festivalId),
  ) satisfies CalculateFestivalCo2;

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!festival) {
    return <h1>{translate('PdfExports.festival-not-found')}</h1>;
  }

  const impactEnvScore100 =
    Math.round((impactEnv.score * 100) / impactEnv.maxScore) || 0;

  return (
    <div id="page3" className="pdf-page">
      <div className="content">
        <div className="pdf-light-header">
          <div>
            <h1>{festival.name}</h1>
            {festival.startDate && festival.endDate && (
              <h2>
                <img
                  src={CalendarMonthSvg}
                  width="19"
                  height="19"
                  style={{ position: 'relative', top: 4, marginRight: 5 }}
                  alt="Calendar icon"
                />
                <span className="festival-date">
                  {formatProjectDatesTz(
                    festival.startDate || '',
                    festival.endDate || '',
                  )}
                </span>
              </h2>
            )}
          </div>
        </div>
        <div className="pdf-separator"></div>

        <div className="model-header">
          <div>
            <h2>{translate('PdfExports.env-score-details')}</h2>
          </div>
          <div>
            <h4>
              <span className="highlight">{impactEnvScore100}</span>/ 100
            </h4>
            <h5>
              {impactEnv.score} / {displayDecimal(impactEnv.maxScore)}
            </h5>
          </div>
          <div>
            <PdfScoreCard
              type="env"
              scoreLetter={impactEnv.scoreLetter}
              size="medium"
            />
          </div>
        </div>

        <div className="model-content">
          <div
            style={{
              width: 360,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <div className="sector">
              <div className="sector-header">
                <h4>{translate('Projects.co2')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(impactCo2.kgeCO2ByParticipant)}
                  </span>{' '}
                  {translate('Projects.kgeCO2-by-participant')}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  <li>
                    <span>{translate('PdfExports.co2-emissions')}</span>
                    <span style={{ fontWeight: 700 }}>
                      {impactEnv.topics.GLOBAL_CO2.score} /{' '}
                      {impactEnv.topics.GLOBAL_CO2.maxScore}
                    </span>
                  </li>
                  <li>{translate('PdfExports.co2-emissons-tipnote')}</li>
                </ul>
                <LittleGauge
                  score={impactEnv.topics.GLOBAL_CO2.score}
                  maxScore={impactEnv.topics.GLOBAL_CO2.maxScore}
                />
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4>{translate('Env.Topics.FOOD_BEVERAGES')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(impactEnv.topics.FOOD_BEVERAGES.score)}
                  </span>{' '}
                  / {displayDecimal(impactEnv.topics.FOOD_BEVERAGES.maxScore)}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      impactEnv.topics.FOOD_BEVERAGES.sections,
                    ) as [
                      keyof typeof impactEnv.topics.FOOD_BEVERAGES.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Env.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {displayDecimal(maxScore)}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={impactEnv.topics.FOOD_BEVERAGES.score}
                  maxScore={impactEnv.topics.FOOD_BEVERAGES.maxScore}
                />
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4>{translate('Env.Topics.RESOURCES_POLLUTION')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(impactEnv.topics.RESOURCES_POLLUTION.score)}
                  </span>{' '}
                  /{' '}
                  {displayDecimal(
                    impactEnv.topics.RESOURCES_POLLUTION.maxScore,
                  )}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      impactEnv.topics.RESOURCES_POLLUTION.sections,
                    ) as [
                      keyof typeof impactEnv.topics.RESOURCES_POLLUTION.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Env.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {displayDecimal(maxScore)}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={impactEnv.topics.RESOURCES_POLLUTION.score}
                  maxScore={impactEnv.topics.RESOURCES_POLLUTION.maxScore}
                />
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4>{translate('Env.Topics.COMMUNICATION')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(impactEnv.topics.COMMUNICATION.score)}
                  </span>{' '}
                  / {displayDecimal(impactEnv.topics.COMMUNICATION.maxScore)}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(impactEnv.topics.COMMUNICATION.sections) as [
                      keyof typeof impactEnv.topics.COMMUNICATION.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Env.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {displayDecimal(maxScore)}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={impactEnv.topics.COMMUNICATION.score}
                  maxScore={impactEnv.topics.COMMUNICATION.maxScore}
                />
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4>{translate('Env.Topics.PUBLIC_ENGAGEMENT')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(impactEnv.topics.PUBLIC_ENGAGEMENT.score)}
                  </span>{' '}
                  /{' '}
                  {displayDecimal(impactEnv.topics.PUBLIC_ENGAGEMENT.maxScore)}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      impactEnv.topics.PUBLIC_ENGAGEMENT.sections,
                    ) as [
                      keyof typeof impactEnv.topics.PUBLIC_ENGAGEMENT.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Env.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {displayDecimal(maxScore)}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={impactEnv.topics.PUBLIC_ENGAGEMENT.score}
                  maxScore={impactEnv.topics.PUBLIC_ENGAGEMENT.maxScore}
                />
              </div>
            </div>
          </div>
          <div
            style={{
              width: 340,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            {' '}
            <div className="model-overall-comments">
              {translate('PdfExports.comment')}
            </div>
          </div>
        </div>

        <div className="navigation-controls">
          <a href="#page2" className="tag-pill">
            {translate('PdfExports.go-back-to-co2')}
          </a>
          <a href="#page4" className="tag-pill">
            {translate('PdfExports.go-to-ess')}
          </a>
        </div>
      </div>

      <PdfProjectPager current={3} total={totalPages}></PdfProjectPager>
    </div>
  );
}
